import type { MessagesQueryParams } from '@/features/message-list/models/message-pagination';
import type { Message } from '@/features/messages';
import { keepPreviousData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { loadMessages } from '../services/message-list.service';
import { insertSeparate } from '../services/message-marker.service';
import { useActionStackStore } from '../stores/action-stack';
import { messageListKeys } from './message-list-keys';

export interface UseMessageListParams {
  conversationId?: Nullable<string>;
  enabled?: boolean;
}

export interface MessageListData {
  pages: Message[][];
  pageParams: MessagesQueryParams[];
}

export const MESSAGES_PAGE_SIZE = 50;
export function useMessageList({ conversationId, enabled = true }: UseMessageListParams) {
  const query = useInfiniteQuery({
    queryKey: messageListKeys.conversation(conversationId!),
    queryFn: async ({ pageParam, signal }: { pageParam: MessagesQueryParams; signal?: AbortSignal }) => {
      return await loadMessages({
        conversationId: conversationId!,
        fromMessageId: pageParam.fromMessageId,
        direction: pageParam.direction || 'OLDER',
        pageSize: MESSAGES_PAGE_SIZE,
        signal
      });
    },
    initialPageParam: { fromMessageId: undefined, direction: 'OLDER' } as MessagesQueryParams,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const cursor = lastPage[lastPage.length - 1]?.messageId || undefined;
      if (
        (!lastPageParam?.fromMessageId && lastPageParam?.direction === 'OLDER') ||
        lastPage.length < lastPageParam.pageSize ||
        lastPage.length <= 1
      ) {
        return undefined;
      }

      return { fromMessageId: cursor, direction: 'NEWER' } as MessagesQueryParams;
    },
    getPreviousPageParam: (firstPage, _, firstPageParam) => {
      const cursor = firstPage[0]?.messageId || undefined;
      if (!cursor || firstPageParam?.fromMessageId === cursor) {
        return undefined;
      }

      return { fromMessageId: cursor, direction: 'OLDER' } as MessagesQueryParams;
    },
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    throwOnError: true,
    placeholderData: keepPreviousData
  });

  const { dispatchAction, pageSize } = useActionStackStore();

  useEffect(() => {
    if (query.isFetched) {
      dispatchAction({ type: 'scrollToBottom', conversationId: conversationId! });
    }
  }, [query.isFetched, dispatchAction, conversationId]);

  const loadNewer = async () => {
    if (!conversationId) return;
    dispatchAction({ type: 'fetchBottom', conversationId, pageSize });
  };

  const loadOlder = async () => {
    if (!conversationId) return;
    dispatchAction({ type: 'fetchTop', conversationId, pageSize });
  };

  const messageData = query.data?.pages.flat() || [];
  const messages: Message[] = useMemo(() => insertSeparate(messageData), [messageData]);

  return {
    // Query state
    ...query,
    query,

    // Processed data
    messages,
    totalCount: messages.length,

    // Actions (dispatched via ActionStack)
    loadNewer,
    loadOlder,

    // Loading states
    isLoadingOlder: query.isFetchingPreviousPage,
    isLoadingNewer: query.isFetchingNextPage,
    canLoadOlder: query.hasPreviousPage,
    canLoadNewer: query.hasNextPage
  };
}

export const useInvalidateMessages = () => {
  const queryClient = useQueryClient();
  const initialPageParam = { fromMessageId: undefined, direction: 'OLDER' } as MessagesQueryParams;
  return (convId: string, initParams?: Partial<MessagesQueryParams>) => {
    queryClient.setQueryData(
      messageListKeys.conversation(convId),
      (): MessageListData => ({
        pages: [],
        pageParams: [{ ...initialPageParam, ...initParams }]
      })
    );

    return queryClient.invalidateQueries({ queryKey: messageListKeys.conversation(convId) });
  };
};
